import React, {
  useMemo,
  MouseEvent,
  ReactNode,
  CSSProperties,
  forwardRef,
  HTMLAttributes,
} from 'react';
import clsx from 'clsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import ToolTip, {
  Props as TooltipProps,
} from 'core/components/Tooltip';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import {
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';

type Props = {
  tagName?: string;
  children?: ReactNode;
  className?: string;
  defaultColor?: string;
  responsive?: boolean;
  hasStripes?: boolean;
  tooltip?: ReactNode | string;
  tooltipProps?: Partial<TooltipProps>;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  style?: CSSProperties;
} & HTMLAttributes<HTMLDivElement>;

const Tag = forwardRef<HTMLDivElement, Props>(({
  children,
  tagName = '',
  className,
  defaultColor = 'bg-emerald-200',
  responsive = false,
  hasStripes = false,
  tooltip,
  tooltipProps = {},
  onClick = noop,
  style,
  ...props
}, ref) => {
  const colorsHashMap = useColorsHashMap();
  const color = useMemo(() => (
    isNil(tagName)
      ? defaultColor
      : colorsHashMap[tagName] ?? defaultColor
  ), [tagName, colorsHashMap, defaultColor]);
  const isSmallScreen = useMediaQuery('(max-width:1200px)');
  const textColor = /bg-(\w+)-[5-9]00$/i.test(color) ? 'text-white' : 'text-black';
  const classes = clsx(
    'populate-tag py-0.5 px-2 !text-xs rounded-full border-0',
    color,
    textColor,
    className,
    {
      'has-stripes font-semibold': hasStripes,
      'small:!h-4 small:!min-w-[1rem] small:block small:!text-[0px]': responsive,
    },
  );

  return (
    <ToolTip
      title={tooltip}
      hide={!(responsive && isSmallScreen) && isNil(tooltip)}
      {...tooltipProps}
    >
      <div
        {...props}
        ref={ref}
        role="button"
        tabIndex={-1}
        className={classes}
        onClick={onClick}
        onKeyDown={noop}
        style={style}
      >
        {children ?? tagName ?? '--'}
      </div>
    </ToolTip>
  );
});

export default Tag;
